import { IntersectionObserverElement, YouTubeEmbededVideo } from '@components/Common';
import { Resource } from '@core';
import { StyleProps, mc } from '@styles';
import { clearHtmlTagText, strToYouTubeId, textToId } from '@utils/str';
import { isArr, isStr, isUnknownDict } from '@utils/types';
import parseHtml, {
  DOMNode,
  domToReact,
  Element as HTMLParserElement,
  HTMLReactParserOptions,
  Text as HTMLParserText,
} from 'html-react-parser';
import { isEmpty } from 'lodash';
import React, { FC } from 'react';

import * as styles from './postContent.module.css';
import PostGetTheAppWidget from '../Widgets/GetTheAppWidget';
import PostMerchantsWidget from '../Widgets/MerchantsWidget';
import PostSingleMerchantWidget from '../Widgets/SingleMerchantWidget';
import { TestIdProps } from '@utils';
import PostPartnerWidget, { PostPartnerWidgetData } from '../Widgets/PatnerWidget';

interface Props extends PostContentOprions, StyleProps, TestIdProps {
  html: string;
}

interface PostContentOprions {
  relatedMerchants?: Resource[];
  elementClassNames?: Record<string, string>;
  onHeaderElementVisibilityChange?: (element: Element, inView: boolean) => void;
}

export const PostContent: FC<Props> = ({
  style,
  className,
  elementClassNames,
  html,
  relatedMerchants,
  onHeaderElementVisibilityChange,
}) => {
  const options: HTMLReactParserOptions = {
    replace: (node: DOMNode) =>
      postHtmlToElements(node, options, {
        onHeaderElementVisibilityChange,
        relatedMerchants,
        elementClassNames,
      }),
  };

  return (
    <div style={style} className={className}>
      {parseHtml(preprocessHtml(html), options)}
    </div>
  );
};

const postHtmlToElements = (
  node: DOMNode,
  options: HTMLReactParserOptions,
  { onHeaderElementVisibilityChange, elementClassNames, relatedMerchants }: PostContentOprions,
) => {
  const attribs = (node as any).attribs;
  const children = (node as any).children;
  const name = (node as any).name;

  if (attribs?.href)
    return (
      <a
        href={attribs?.href}
        target="_blank"
        className={elementClassNames?.a ? elementClassNames?.a : 'hover:text-accent-orange underline font-bold'}
      >
        {domToReact(children, options)}
      </a>
    );

  // H elements
  if (onHeaderElementVisibilityChange) {
    if (/[Hh]\d/g.test(name)) {
      const elementText = clearHtmlTagText(domNodeToText(node));
      if (!elementText) return <></>;
      const elementName = name.toLowerCase();
      return (
        <IntersectionObserverElement
          Tag={elementName}
          className={mc('mt-10 mb-3 xl:mt-10 xl:mb-3 p-0', elementClassNames?.[elementName])}
          attributes={{ id: textToId(domNodeToText(node)) }}
          onHeaderElementVisibilityChange={onHeaderElementVisibilityChange}
        >
          {domToReact(children, options)}
        </IntersectionObserverElement>
      );
    }
  }

  switch (name) {
    case 'blockquote':
      return (
        <blockquote
          data-testid="post-blockquote"
          style={{ color: '#2D4C60' }}
          className={mc(
            'my-8 p-5 xl:my-10 xl:px-8 xl:py-6',
            'bg-seafoam',
            'font-literata font-medium rounded-lg text-xl not-italic',
            elementClassNames?.blockquote,
          )}
        >
          {domToReact(children, options)}
        </blockquote>
      );
    case 'p': {
      if (isEmpty(children)) return <></>;
      const elementText = clearHtmlTagText(domNodeToText(node));
      // Widgets
      if (elementText) {
        const shortcode = elementText;
        if (shortcode === 'add_app')
          return <PostGetTheAppWidget testId="post-get-the-app-widget" className="mt-8 mb-8 xl:mt-20 xl:mb-16" />;
        if (relatedMerchants && relatedMerchants.length > 0) {
          // add_merchants
          if (shortcode === 'add_merchants' && relatedMerchants.length >= 3)
            return (
              <PostMerchantsWidget
                testId="post-merchants-widget"
                className={mc('mt-8 mb-8 xl:mb-16 xl:mt-20', '-mx-8 sm:mx-0')}
                merchants={relatedMerchants.slice(0, 3)}
              />
            );
          // add_1merchant
          if (shortcode === 'add_1merchant')
            return (
              <PostSingleMerchantWidget
                testId="post-single-merchant-widget"
                className={mc('mt-8 mb-8 xl:mb-16 xl:mt-16')}
                merchant={relatedMerchants[0]}
              />
            );
          // add_5merchants
          if (shortcode === 'add_5merchants' && relatedMerchants.length >= 5)
            return (
              <PostMerchantsWidget
                testId="post-merchants-slider-widget"
                className={mc('mt-8 mb-8 xl:mb-16 xl:mt-20', '-mx-8 sm:mx-0')}
                merchants={relatedMerchants}
              />
            );
          // add_merchant1, add_merchant2, ...
          const singhleMerchantMatch = shortcode.match(/add_merchant(\d+)/);
          if (singhleMerchantMatch && singhleMerchantMatch[1]) {
            const merchantIndex = parseInt(singhleMerchantMatch[1], 10) - 1;
            if (merchantIndex >= 0 && merchantIndex < relatedMerchants.length)
              return (
                <PostSingleMerchantWidget
                  testId="post-single-merchant-widget"
                  className={mc('mt-8 mb-8 xl:mb-16 xl:mt-16')}
                  merchant={relatedMerchants[merchantIndex]}
                />
              );
          }
        }
        // Partner widget
        if (shortcode.startsWith('{"partner":')) {
          const widgetData = parsePartnerShortcodeData(shortcode);
          if (widgetData) {
            return <PostPartnerWidget className={mc('mt-8 mb-8 xl:mb-16 xl:mt-16')} {...widgetData} />;
          } else {
            return <></>;
          }
        }
      }
      // YouTube video
      const youtubeId = strToYouTubeId(elementText);
      if (youtubeId) {
        return <YouTubeEmbededVideo className="mt-3 xl:mt-6" videoId={youtubeId} />;
      }
      return (
        <p
          className={mc(
            'mt-3 xl:mt-6',
            'text-dark-gray font-medium text-lg leading-8',
            'whitespace-normal break-words',
            elementClassNames?.p,
          )}
        >
          {domToReact(children, options)}
        </p>
      );
    }
    case 'table': {
      return (
        <div className={mc('mt-3 xl:mt-6 -mx-8 px-8 md:mx-0 md:px-0', 'overflow-x-auto', elementClassNames?.table)}>
          <table className={mc('w-full', 'border', 'leading-8', styles.table)} style={{ minWidth: 565 }}>
            {domToReact(children, options)}
          </table>
        </div>
      );
    }
    case 'td': {
      return <td className={mc('border', 'p-2', elementClassNames?.td)}>{domToReact(children, options)}</td>;
    }
    case 'br':
      return <></>;
    case 'ol':
      return (
        <ol data-testid="post-content-ol" className={mc('mt-3 xl:mt-6', elementClassNames?.ol)}>
          {domToReact(children, options)}
        </ol>
      );
    case 'ul':
      return (
        <ul data-testid="post-content-ul" className={mc('pt-3 xl:pt-6', elementClassNames?.ul)}>
          {domToReact(children, options)}
        </ul>
      );
    case 'li':
      return (
        <li className={mc('text-lg font-medium text-dark-gray', 'leading-8', elementClassNames?.li)}>
          <span>{domToReact(children, options)}</span>
        </li>
      );
    case 'figure':
      return (
        <div
          data-testid="post-content-figure"
          className={mc('mt-3 xl:mt-6', 'rounded-lg overflow-hidden', elementClassNames?.figure)}
        >
          <figure>{domToReact(children, options)}</figure>
        </div>
      );
  }
};

// Preprocessor

const preprocessHtml = (html: string): string => {
  let mod = html;
  mod = replacePartnerShortcode(mod);
  return mod;
};

// Parnter widget

const replacePartnerShortcode = (html: string): string => {
  let mod = html;
  const shortcodeReg = /<p[^>]*>\[partner\]<\/p[^>]*>([\s\S]+?)<p[^>]*>\[partner\]<\/p[^>]*>/g;
  const shortcodeMatch = mod.matchAll(shortcodeReg);
  for (const match of shortcodeMatch) {
    const shortcode = match[0];
    let content = match[1];
    const data: PostPartnerWidgetData = {};
    // Extract images
    const imgReg = /<figure><img[^>]*src="([^"]*)"[^>]*\/?><\/figure>/g;
    const imgMatches = content.matchAll(imgReg);
    for (const imgMatch of imgMatches) {
      if (data.logo && data.sublogo) break;
      const img = imgMatch[0];
      const imgSrc = imgMatch[1];
      if (!imgSrc) continue;
      if (!data.logo) {
        data.logo = encodeURIComponent(imgSrc);
      } else if (!data.sublogo) {
        data.sublogo = encodeURIComponent(imgSrc);
      }
      content = content.replace(img, '');
    }
    // Extract title and description
    const titleReg = /<h3[^>]*>([\s\S]+?)<\/h3>([\s\S]*?)$/g;
    const titleMatch = titleReg.exec(content);
    if (titleMatch) {
      data.title = encodeURIComponent(titleMatch[1]);
      data.description = encodeURIComponent(titleMatch[2]);
      content = content.replace(titleMatch[0], '');
    }
    // Extract button
    const btnReg = /<a[^>]*href="([^"]*)"[^>]*>([\s\S]+?)<\/a>/g;
    const btnMatch = btnReg.exec(content);
    if (btnMatch) {
      data.btn = {
        url: encodeURIComponent(btnMatch[1]),
        label: encodeURIComponent(btnMatch[2]),
      };
      content = content.replace(btnMatch[0], '');
    }
    // Extract short description
    const shortDescriptionReg = /<p[^>]*>([\s\S]+?)<\/p>/g;
    const shortDescriptionMatch = shortDescriptionReg.exec(content);
    if (shortDescriptionMatch) {
      data.shortDescription = encodeURIComponent(shortDescriptionMatch[1]);
      content = content.replace(shortDescriptionMatch[0], '');
    }
    // Replace big shortcode with a small one
    mod = mod.replace(shortcode, `<p>${JSON.stringify({ partner: data })}</p>`);
  }
  return mod;
};

const parsePartnerShortcodeData = (text: string): PostPartnerWidgetData | undefined => {
  try {
    const parsedData = JSON.parse(text);
    if (typeof parsedData !== 'object' || !parsedData) return;
    const partnerData = parsedData.partner;
    if (!isUnknownDict(partnerData)) return undefined;
    const data = partnerData;
    const patner: PostPartnerWidgetData = {};
    if (isStr(data.logo)) patner.logo = decodeURIComponent(data.logo);
    if (isStr(data.sublogo)) patner.sublogo = decodeURIComponent(data.sublogo);
    if (isStr(data.shortDescription)) patner.shortDescription = decodeURIComponent(data.shortDescription);
    if (isStr(data.title)) patner.title = decodeURIComponent(data.title);
    if (isStr(data.description)) patner.description = decodeURIComponent(data.description);
    const btn = data.btn;
    if (isUnknownDict(btn)) {
      if (isStr(btn.url) && isStr(btn.label))
        patner.btn = { url: decodeURIComponent(btn.url), label: decodeURIComponent(btn.label) };
    }
    return patner;
  } catch (e: unknown) {
    return undefined;
  }
};

// Utils

const domNodeToText = (node: DOMNode): string => {
  if (Array.isArray(node)) return node.map(domNodeToText).join('');
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return `${node}`;
  if (!node) return '';
  if (isHTMLParserElement(node)) return node.children.map(domNodeToText).join('');
  if (isHTMLParserText(node)) return node.data;
  return '';
};

export const isHTMLParserElement = (val: unknown): val is HTMLParserElement => !!val && isUnknownDict(val) && isArr(val.children);
export const isHTMLParserText = (val: unknown): val is HTMLParserText => !!val && isUnknownDict(val) && val.type === 'text';

// Author

const authorHtmlToReactElements = ({ children, name, options }: any) => {
  switch (name) {
    case 'p': {
      if (isEmpty(children)) return <></>;
      return (
        <span className="whitespace-normal break-words text-ocean-blue font-medium leading-8 mt-3 xl:mt-6">
          {domToReact(children, options)}
        </span>
      );
    }
    case 'br':
      return <br />;
    default:
      return null;
  }
};

export const authorHtmlParserOptions: HTMLReactParserOptions = {
  replace: ({ attribs, children, name }: any) =>
    authorHtmlToReactElements({
      attribs,
      children,
      name,
      options: authorHtmlParserOptions,
    }),
};

// Headers

export interface PostHeaderElement {
  id: string;
  tag: string;
  level: number;
  text: string;
  subitems: PostHeaderElement[];
}

export const getHtmlHeadersTree = (html: string): PostHeaderElement[] => {
  const regexp = /<h([1-6])>(.*?)<\/h[1-6]>/g;
  let match = regexp.exec(html);
  if (!match) return [];
  const res: PostHeaderElement[] = [];
  while (match) {
    const tag = `h${match[1]}`;
    const level = parseInt(match[1], 10);
    const id = textToId(match[2]);
    const text = clearHtmlTagText(match[2]);
    if (text) res.push({ id, tag, level, text, subitems: [] });
    match = regexp.exec(html);
  }
  return buildHtmlHeadersTree(res);
};

const buildHtmlHeadersTree = (headers: PostHeaderElement[]): PostHeaderElement[] => {
  const res: PostHeaderElement[] = [];
  for (let i = 0; i < headers.length; i++) {
    const itm = headers[i];
    if (!res.length) {
      res.push(itm);
      continue;
    }
    const prev = res[res.length - 1];
    if (itm.level > prev.level) {
      prev.subitems.push(itm);
    } else if (itm.level === prev.level) {
      res.push(itm);
      continue;
    } else if (itm.level < prev.level) {
      let parent = res[res.length - 2];
      while (parent && parent.level >= itm.level) {
        res.pop();
        parent = res[res.length - 2];
      }
      if (parent) {
        parent.subitems.push(itm);
      } else {
        res.push(itm);
      }
    }
  }
  return res;
};

// Shortcodes

type GatherContentShortcode = 'add_app' | 'add_merchants';

export const isPostContentContainShortcode = (content: string, shortcode: GatherContentShortcode): boolean =>
  content.indexOf(shortcode) !== -1;
